import { Button } from "@/components/ui/Button";
import { Card, CardDescription, CardHeader } from "@/components/ui/Card";
import { BodyText } from "@/components/ui/Text";
import { cn } from "@/lib/utils";
import { useNavigate, useParams } from "@tanstack/react-router";
import { formatDate } from "date-fns";
import type {
  AttachmentUniversalChatMessage,
  ChatParticipant,
} from "../types/universalChatTypes";
import { TimestampWithStatus } from "./TimestampWithStatus";

interface AttachmentMessageProps {
  message: AttachmentUniversalChatMessage;
  includeAvatar?: boolean;
  participant?: ChatParticipant;
}

export const AttachmentMessage = ({
  message,
  participant,
}: AttachmentMessageProps) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const navigate = useNavigate();

  const senderType = message.senderType;
  const isPatient = senderType === "patient";
  const isSystem = senderType === "system";
  const isImage = message.body.contentType.includes("image");
  const documentReferenceId = message.body.documentReferenceId;
  const hasAttachmentLink = Boolean(documentReferenceId || message.body.url);

  const handleViewAttachment = () => {
    if (documentReferenceId && patientId) {
      navigate({
        to: "/patient/$patientId/files/$fileId",
        params: {
          patientId,
          fileId: documentReferenceId,
        },
      });
      return;
    }

    if (message.body.url) {
      window.open(message.body.url, "_blank", "noopener,noreferrer");
    }
  };

  // System attachments -- we don't use these but just in case
  if (isSystem) {
    return (
      <div className="flex flex-col justify-center items-center">
        <BodyText size="xs" textColor="primary" italic>
          System attachment.
        </BodyText>
        {hasAttachmentLink && (
          <Button
            variant="primary"
            size="xs"
            className="m-2"
            onClick={handleViewAttachment}
          >
            View
          </Button>
        )}
        <BodyText size="xs" textColor="tertiary">
          {formatDate(new Date(message.createdAt), "MM/dd/yyyy h:mm a")}
        </BodyText>
      </div>
    );
  }

  // Human attachments - aligned based on sender
  return (
    <div className="flex flex-col">
      <Card className={cn("w-[70%]", isPatient ? "mr-auto" : "ml-auto")}>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardDescription>
            {participant?.displayName || "Patient"} sent{" "}
            {isImage ? "an image" : "a document"}.
          </CardDescription>
          {hasAttachmentLink && (
            <Button variant="primary" size="xs" onClick={handleViewAttachment}>
              View in Files
            </Button>
          )}
        </CardHeader>
      </Card>
      {/* Timestamp */}
      {isPatient ? (
        <div className={cn("text-left")}>
          <BodyText size="xs" textColor="tertiary">
            {formatDate(new Date(message.createdAt), "MM/dd/yyyy h:mm a")}
          </BodyText>
        </div>
      ) : (
        <TimestampWithStatus
          timestamp={message.createdAt}
          status={message.processingStatus ?? null}
          alignmentClass={"text-right"}
        />
      )}
    </div>
  );
};
